<template>
  <div :class="classList">
    <label v-if="label">
      {{ label }}
      <span v-if="isRequired" class="asterisk-mark">*</span>
    </label>
    <div
      v-if="adminDescription && $store.getters.isAgent"
      class="admin-description"
    >
      <div v-html="adminDescription"></div>
    </div>
    <div v-if="description" class="description">
      <div v-html="description"></div>
    </div>
    <div class="day-hour-list">
      <div
        class="row placement-time-row"
        v-for="(workday, index) of normalizedListDays"
        :key="index"
      >
        <div class="day-checkbox-container col-md-3" :class="colClass">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              v-model="listDayCheck[index]"
              @change="handleDataChange()"
              :id="'workday' + index"
            />
          </div>
          <label class="workday-label non-search-label" :for="'workday' + index">
            {{ workday }}
          </label>
        </div>
        <div class="time-from-container col-md-4 col-sm-6" :class="colClass">
          <div v-if="useTimePicker" class="timepicker-from-container">
            <div class="from-label">{{ fromLabel }}</div>
            <TimePickerWithLabel
              v-model="listFrom[index]"
              classList=""
              :isDisabled="listDayCheck[index] !== true"
              :minutesInterval="minutesInterval"
              :is24HoursFormat="is24HoursFormat"
              :submitClicked="submitClicked"
              @input="handleDataChange()"
            />
          </div>
          <div v-else>
            <input
              type="text"
              class="form-control"
              v-model="listFrom[index]"
              :disabled="listDayCheck[index] !== true"
              :submitClicked="submitClicked"
              :placeholder="fromLabel"
              @input="handleDataChange()"
            />
            <p
              class="required-message"
              v-if="
                submitClicked &&
                isRequired &&
                listDayCheck[index] &&
                !listFrom[index]
              "
            >
              {{ getLabelByKeys("ERROR_MESSAGES.FIELD_REQUIRED") }}
            </p>
          </div>
        </div>
        <div class="time-to-container col-md-4 col-sm-6" :class="colClass">
          <div v-if="useTimePicker" class="timepicker-to-container">
            <div class="to-label">{{ toLabel }}</div>
            <TimePickerWithLabel
              classList=""
              v-model="listTo[index]"
              :isDisabled="listDayCheck[index] !== true"
              :minutesInterval="minutesInterval"
              :is24HoursFormat="is24HoursFormat"
              :submitClicked="submitClicked"
              @input="handleDataChange()"
            />
          </div>
          <div v-else>
            <input
              type="text"
              class="form-control"
              v-model="listTo[index]"
              :disabled="listDayCheck[index] !== true"
              :submitClicked="submitClicked"
              :placeholder="toLabel"
              @input="handleDataChange()"
            />
            <p
              class="required-message"
              v-if="
                submitClicked &&
                isRequired &&
                listDayCheck[index] &&
                !listTo[index]
              "
            >
              {{ getLabelByKeys("ERROR_MESSAGES.FIELD_REQUIRED") }}
            </p>
          </div>
        </div>
        <div
          class="total-hours-container"
          :class="colClass"
          v-if="useTimePicker && !hasNote && !hasTimeOff"
        >
          <div class="total-label" v-if="calculateTotalTime(index)">
            {{ totalLabel }}
          </div>
          <div class="total-value">
            {{ calculateTotalTime(index) }}
          </div>
        </div>
        <div class="time-off-container" :class="colClass" v-if="hasTimeOff">
          <input
            type="text"
            class="form-control"
            v-model="listTimeOff[index]"
            :disabled="listDayCheck[index] !== true"
            :submitClicked="submitClicked"
            :placeholder="timeoffLabel"
            @input="handleDataChange()"
          />
          <p
            class="required-message"
            v-if="
              submitClicked &&
              isRequired &&
              listDayCheck[index] &&
              !listTimeOff[index]
            "
          >
            {{ getLabelByKeys("ERROR_MESSAGES.FIELD_REQUIRED") }}
          </p>
        </div>
        <div class="note-container" :class="colClass" v-if="hasNote">
          <input
            type="text"
            class="form-control"
            v-model="listNote[index]"
            :disabled="listDayCheck[index] !== true"
            :submitClicked="submitClicked"
            :placeholder="noteHeader"
            @input="handleDataChange()"
          />
        </div>
      </div>
    </div>
    <p
      class="required-message"
      v-if="!listDayCheck.includes(true) && submitClicked && isRequired"
    >
      {{ getLabelByKeys("ERROR_MESSAGES.FIELD_REQUIRED") }}
    </p>
  </div>
</template>
<script>
/* This component is used to select working hours (from ... to ...) for each working day (From Monday to Friday)*/
import { TIME_FROM_TO_SEPARATOR } from "../../../constants/separator";
import TimePickerWithLabel from "../form-elements/TimePickerWithLabel.vue";

export default {
  name: "DayHoursInput",
  props: {
    /* Name of the date picker component, currently, which is unused*/
    name: String,
    // Label at the top of component
    label: String,
    // Description which are only visible to admin
    adminDescription: String,
    // Description below label
    description: String,
    // List of class to style for component, each class is separated by space
    classList: {
      type: String,
      default: "col-lg-12",
    },
    // Input value, which is used for v-model
    value: Array,
    // Mark if the field is required
    isRequired: {
      type: Boolean,
      default: true,
    },
    // true if submit button is clicked, it is used to show 'required' error
    submitClicked: {
      type: Boolean,
      default: false,
    },
    // true if disable the timepicker
    listDays: [String, Array],
    // true if has time off input
    hasTimeOff: {
      type: Boolean,
      default: false,
    },
    hasNote: {
      type: Boolean,
      default: false,
    },
    customLabels: {
      type: [String, Array, Boolean],
    },
    useTimePicker: {
      type: Boolean,
      default: false,
    },
    minutesInterval: {
      type: Number,
      default: 15,
    },
    is24HoursFormat: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TimePickerWithLabel,
  },
  data() {
    return {
      listDayCheck: [],
      listFrom: [],
      listTo: [],
      listTimeOff: [],
      listNote: [],
    };
  },
  created() {
    this.initialzeData();
  },
  computed: {
    fromLabel() {
      return this.normalizedCustomLabel[1]
        ? this.normalizedCustomLabel[1]
        : "From";
    },
    toLabel() {
      return this.normalizedCustomLabel[2]
        ? this.normalizedCustomLabel[2]
        : "To";
    },
    totalLabel() {
      return this.normalizedCustomLabel[3]
        ? this.normalizedCustomLabel[3]
        : "Total";
    },
    timeoffLabel() {
      return this.normalizedCustomLabel[3]
        ? this.normalizedCustomLabel[3]
        : "Daily time off";
    },
    colClass() {
      if (this.hasNote) {
        if (this.hasTimeOff) {
          return "col-lg-custom-24";
        } else {
          return "col-lg-3";
        }
      } else {
        if (this.hasTimeOff) {
          return "col-lg-3";
        } else {
          if (this.useTimePicker) {
            // Show total time column
            return "col-lg-3";
          } else {
            return "col-lg-4";
          }
        }
      }
    },
    noteHeader() {
      if (this.hasTimeOff) {
        if (this.normalizedCustomLabel[4]) {
          return this.normalizedCustomLabel[4];
        } else {
          return "Note";
        }
      } else {
        if (this.normalizedCustomLabel[3]) {
          return this.normalizedCustomLabel[3];
        } else {
          return "Note";
        }
      }
    },
    normalizedCustomLabel() {
      if (!this.customLabels) {
        return [];
      }
      if (Array.isArray(this.customLabels)) {
        return this.customLabels;
      }
      return this.customLabels
        .split("\n")
        .map((option) => option.trim())
        .filter((option) => option.length > 0);
    },
    normalizedListDays() {
      if (!this.listDays) {
        return [];
      }
      if (Array.isArray(this.listDays)) {
        return this.listDays;
      }
      return this.listDays
        .split("\n")
        .map((option) => option.trim())
        .filter((option) => option.length > 0);
    },
  },
  methods: {
    initialzeData() {
      this.listDayCheck = [];
      this.listFrom = [];
      this.listTo = [];
      this.listTimeOff = [];
      this.listNote = [];
      if (this.value == null || this.value.length === 0) {
        return;
      }
      for (const val of this.value) {
        if (val == null || val === "") {
          this.listDayCheck.push(null);
          this.listFrom.push(null);
          this.listTo.push(null);
          this.listTimeOff.push(null);
          this.listNote.push(null);
        } else {
          this.listDayCheck.push(true);
          let fromToTokens;
          if (val.includes(TIME_FROM_TO_SEPARATOR)) {
            // New data is separated by tab (use with Textbox)
            fromToTokens = val.split(TIME_FROM_TO_SEPARATOR);
          } else {
            if (!this.useTimePicker) {
              // Old data is separated by comma (use with Timepicker)
              const mapReplacedValues = {
                ":AM": " AM",
                ":PM": " PM",
              };
              fromToTokens = this.replaceAllByMap(val, mapReplacedValues).split(
                ","
              );
            }
            fromToTokens = val.split(",");
            // Only get first 2 items
            fromToTokens = fromToTokens.slice(0, 2);
          }
          // Parse from
          if (this.useTimePicker) {
            if (fromToTokens[0]) {
              this.listFrom.push(this.parseTime(fromToTokens[0]));
            } else {
              this.listFrom.push(null);
            }
          } else {
            let from = fromToTokens[0];
            if (from === "Unspecified Time") {
              from = "";
            }
            this.listFrom.push(from);
          }
          // Parse to
          if (this.useTimePicker) {
            if (fromToTokens[1]) {
              this.listTo.push(this.parseTime(fromToTokens[1]));
            } else {
              this.listTo.push(null);
            }
          } else {
            if (fromToTokens.length < 2) {
              this.listTo.push(null);
              continue;
            }
            let to = fromToTokens[1];
            if (to === "Unspecified Time") {
              to = "";
            }
            this.listTo.push(to);
          }
          if (this.hasTimeOff) {
            if (fromToTokens.length < 3) {
              this.listTimeOff.push(null);
              continue;
            }
            let timeOff = fromToTokens[2];
            if (timeOff === "Unspecified") {
              timeOff = "";
            }
            this.listTimeOff.push(timeOff);
          }
          if (this.hasNote) {
            const noteIndex = this.hasTimeOff ? 3 : 2;
            if (fromToTokens.length < noteIndex + 1) {
              this.listNote.push("");
              continue;
            }
            let note = fromToTokens[noteIndex];
            this.listNote.push(note);
          }
        }
      }
    },
    handleDataChange() {
      let result = [];
      for (let index = 0; index < this.normalizedListDays.length; index++) {
        const dayCheck = this.listDayCheck[index];
        if (dayCheck !== true) {
          result.push("");
          continue;
        }
        let from = this.listFrom[index];
        let to = this.listTo[index];
        let fromTo;
        if (this.useTimePicker) {
          fromTo =
            this.convertTimeToString(from) +
            TIME_FROM_TO_SEPARATOR +
            this.convertTimeToString(to);
        } else {
          if (!from) {
            from = "Unspecified Time";
          }
          if (!to) {
            to = "Unspecified Time";
          }
          fromTo = from + TIME_FROM_TO_SEPARATOR + to;
        }
        if (this.hasTimeOff) {
          let hasTimeOff = this.listTimeOff[index];
          if (!hasTimeOff) {
            hasTimeOff = "Unspecified";
          }
          fromTo += TIME_FROM_TO_SEPARATOR + hasTimeOff;
        }
        if (this.hasNote) {
          let note = this.listNote[index];
          if (!note) {
            note = "";
          }
          fromTo += TIME_FROM_TO_SEPARATOR + note;
        }
        result.push(fromTo);
      }
      this.$emit("input", result);
    },
    calculateTotalTime(index) {
      const fromAMPM = this.listFrom[index];
      const toAMPM = this.listTo[index];
      if (fromAMPM == null || toAMPM == null) {
        return;
      }
      return this.calculateDiffTime(fromAMPM, toAMPM);
    },
    parseTime(timeString) {
      /* Parse time from String to Object
        Input: '00:11:AM
        Output: {hh: 00, mm: 11, A: AM}
      */
      const tokens = timeString.split(":");
      if (tokens.length != 3) {
        return null;
      } else {
        return { hh: tokens[0], mm: tokens[1], A: tokens[2] };
      }
    },
    convertTimeToString(time) {
      /* Convert time from Object to String
        Input: {hh: 00, mm: 11, A: AM}
        Output: '00:11:AM
      */
      if (time == null) {
        return "";
      }
      return time.hh + ":" + time.mm + ":" + time.A;
    },
  },
};
</script>

<style scoped>
.day-checkbox-container {
  display: flex;
  align-items: center;
}

.placement-time-row {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.day-checkbox-container .form-check-input {
  margin-top: 0;
}

.day-checkbox-container > .form-check {
  height: 18px;
}

.workday-label {
  margin-top: 0;
  font-weight: normal;
  margin-bottom: 0.5em;
}

.day-hours-selector-header {
  margin-bottom: 0.5em;
}

@media only screen and (min-width: 768px) {
  .time-from-container,
  .time-to-container,
  .total-hours-container,
  .time-off-container,
  .note-container {
    padding-left: 1em;
  }

  .total-hours-container {
    text-align: center;
  }
}

.timepicker-from-container {
  display: flex;
  align-items: center;
}

.from-label {
  margin-right: 0.5em;
}

.timepicker-to-container {
  display: flex;
  align-items: center;
}

.to-label {
  margin-right: 0.5em;
  margin-left: 1.3em;
}

.total-hours-container {
  display: flex;
  align-items: center;
}

.total-label {
  margin-right: 0.5em;
}
</style>



// WEBPACK FOOTER //
// src/components/common/complex-input-elements/DayHoursInput.vue